/**
 * Which strings get bundled together, as a plain mapping.
 *
 * A namespace is a chunk of the translation catalog that loads as a unit. This mapping is the
 * whole definition — namespaces are not a directory layout, and there are no per-namespace files
 * anywhere in the tree. One `/locales/{locale}.po` covers the entire monorepo and goes to Crowdin
 * as a single file; the Vite plugin in `@web-monorepo/i18n/vite` slices it into chunks at build time
 * using this mapping and the `#:` origin comments Lingui writes into the PO. Retuning the split is
 * an edit here and a rebuild: no re-extraction, no new Crowdin file, nothing to migrate.
 *
 * Keys are namespace names, values are repo-relative source-path prefixes — repo-relative because
 * the catalog is repo-wide, so a namespace can just as easily be drawn from a shared package as
 * from this app. A string belongs to the first namespace whose prefix matches the file that
 * *defines* it, so a string in a shared component lands in one place no matter which routes
 * render it. Anything unmatched is `common`, which every session loads — so keep that to
 * genuinely shared strings. So is a string whose identical text is defined in files from more
 * than one namespace, since `common` is the only chunk all of their routes load.
 *
 * Audience is the useful axis: a parent never downloads the tutor console's strings.
 */
export const NAMESPACES: Record<string, readonly string[]> = {
  tutor: [
    "marketplace/src/pages/tutor/",
    "marketplace/src/pages/tutor.",
    "marketplace/src/pages/teachers.",
    "marketplace/src/pages/teachers/",
  ],
  parents: ["marketplace/src/pages/parents/", "marketplace/src/pages/parents."],
};

/**
 * Files under a namespace's prefix whose strings still have to be in `common`, because they render
 * outside it: the app-wide modal registry (`marketplace/src/pages/_components/modals`) mounts these
 * on any route, `/search` and `/confirm-details` included. The build fails when this is missing an
 * entry, so this list does not need to be maintained by hand-auditing imports (see
 * `@web-monorepo/i18n/vite`).
 */
export const SHARED: readonly string[] = [
  "marketplace/src/pages/parents/_components/AddChildModal/",
  "marketplace/src/pages/parents/_components/ParentSubjectGradeModal/",
  "marketplace/src/pages/parents/_components/SpecializationSelectionModal/",
];

/** The implicit namespace for everything unmatched. Always loaded. */
export const COMMON_NAMESPACE = "common";

export const ALL_NAMESPACES = [COMMON_NAMESPACE, ...Object.keys(NAMESPACES)];

/**
 * `filePath` is repo-relative, exactly as the PO's `#:` origin records it
 * (`marketplace/src/pages/tutor/index.tsx`). A leading slash is tolerated so callers can pass a
 * path through unchanged; see ./runtime.ts for how a router module key — which is app-relative —
 * becomes one of these.
 */
export function namespaceForFile(filePath: string): string {
  const normalized = filePath.replace(/^\/+/, "");
  if (SHARED.some((prefix) => normalized.startsWith(prefix))) return COMMON_NAMESPACE;
  const match = Object.entries(NAMESPACES).find(([, prefixes]) =>
    prefixes.some((prefix) => normalized.startsWith(prefix)),
  );
  return match?.[0] ?? COMMON_NAMESPACE;
}
